/**
 * Where the camera stands, as plain numbers.
 *
 * Its own module rather than constants inside `engine.ts` because **the minimap needs the
 * yaw and must not import Babylon to get it**. `engine.ts` pulls in the whole engine plus
 * seven static shader modules; `ui/minimap.ts` is pure arithmetic tested in jsdom, and a
 * transitive Babylon import would drag a WebGL library into every HUD test to read one
 * angle.
 *
 * The alternative was copying the number, and a camera yaw copied into the minimap is a
 * second source of truth for *which way is forward* — it would drift the first time the
 * camera moved, and the symptom would be a map that is subtly rotated against the world,
 * which reads as a game bug rather than a stale constant.
 */

/**
 * The camera's yaw, in radians, as an `ArcRotateCamera` alpha.
 *
 * Fixed isometric: there are no camera controls. **Everything that converts between world
 * space and something the player reads on screen must be derived from this**, which since
 * 2026-08-08 includes the minimap's rotation.
 */
export const CAMERA_ALPHA = -Math.PI / 4;

/** The camera's pitch. Also fixed. */
export const CAMERA_BETA = Math.PI / 4;

/** How far back it sits, in world units. */
export const CAMERA_RADIUS = 26;

/** How fast the camera catches up to the player, in e-foldings per second. */
export const CAMERA_FOLLOW_RATE = 8;

/**
 * The unit world direction that points to the **right of the screen**, on the ground.
 *
 * For an `ArcRotateCamera` the horizontal look direction is `(-cos α, -sin α)` in `(x, z)`,
 * and screen-right is that turned a quarter turn about the vertical: `(-sin α, cos α)`.
 * At the shipped `-π/4` this is the `+x +z` diagonal, which is the whole reason the
 * minimap needed rotating — **walking left moved the player up-and-left in world axes**,
 * so an unrotated map drew them going somewhere the screen disagreed with.
 */
export function screenRightOnGround(alpha: number = CAMERA_ALPHA): { x: number; z: number } {
  return { x: -Math.sin(alpha), z: Math.cos(alpha) };
}

/**
 * The unit world direction that points **up the screen**, on the ground — away from the
 * camera, which is where "forward" is for a player reading the world.
 */
export function screenUpOnGround(alpha: number = CAMERA_ALPHA): { x: number; z: number } {
  return { x: -Math.cos(alpha), z: -Math.sin(alpha) };
}
